/** Профили CAdES, которые знает сервер. */
export type CadesProfile = "CAdES-BES" | "CAdES-T" | "CAdES-X Long Type 1";

/**
 * Сертификат для выбора.
 *
 * Только открытые сведения: закрытый ключ и тело сертификата адаптер наружу не
 * отдаёт, а подпись выполняется внутри плагина.
 */
export interface SigningCertificate {
  /** Отпечаток: по нему адаптер находит сертификат в хранилище. */
  readonly id: string;
  readonly subject: string;
  readonly issuer: string;
  /** ISO 8601. */
  readonly validFrom: string;
  /** ISO 8601. */
  readonly validTo: string;
}

/** Почему подписание не состоялось на стороне браузера. */
export type CryptoProFailure =
  | "plugin_unavailable"
  | "certificate_not_found"
  | "profile_unsupported"
  | "user_cancelled"
  | "signing_failed";

export class CryptoProError extends Error {
  readonly name = "CryptoProError";

  constructor(
    readonly reason: CryptoProFailure,
    message: string,
    options?: { cause?: unknown },
  ) {
    super(message, options);
  }
}

export function isCryptoProError(error: unknown): error is CryptoProError {
  return error instanceof CryptoProError;
}

/**
 * Граница между интерфейсом и браузерным плагином КриптоПро.
 *
 * Интерфейс знает только эти операции и не зависит от устройства самого
 * плагина: в тестах подставляется поддельный адаптер, в приложении — реальный.
 */
export interface CryptoProAdapter {
  /** Профили, которые этот браузер умеет создавать. */
  readonly supportedProfiles: readonly CadesProfile[];

  /**
   * Убедиться, что плагин установлен и отвечает.
   *
   * @throws CryptoProError с причиной `plugin_unavailable`
   */
  ensureAvailable(): Promise<{ readonly version: string | null }>;

  /** Действующие сертификаты пользователя, для которых есть закрытый ключ. */
  listCertificates(): Promise<readonly SigningCertificate[]>;

  /**
   * Отделённая подпись CAdES над содержимым, base64.
   *
   * Закрытый ключ остаётся в плагине или на носителе.
   */
  signDetached(input: {
    readonly certificateId: string;
    readonly content: ArrayBuffer;
    readonly profile: CadesProfile;
  }): Promise<string>;
}
